/**
 * Alle im Modell vorkommenden Eigenschaftsnamen in kanonischer PascalCase-Schreibweise.
 *
 * Grundlage der Nachschlagetabelle in `src/api/jsonNaming.ts`: Die API serialisiert camelCase,
 * unsere Typen führen PascalCase. Aus `uri` ist `URI` nicht berechenbar (`Uri` wäre falsch),
 * ebenso nicht `DL` aus `dl` — die Rückrichtung braucht deshalb die Liste der kanonischen Namen.
 *
 * Die vier Domänenentitäten werden hier **nicht** erneut aufgezählt, sondern aus den bereits
 * compilergeprüften Feldtabellen (`fieldTypes/*`) übernommen; damit gibt es nur eine Namensquelle.
 * Für die System- und Transporttypen stehen die Listen hier — als `Record<keyof T, true>` wie in
 * `fieldTypes/`, damit der Compiler jede Eigenschaft verlangt und unbekannte ablehnt.
 */

import type {
  BestellpositionDivideDefinition,
  ChinaAppValidationStatus,
  ContainerCloneDefinition,
  ContainerCloneDefinitionItem,
} from './definitions';
import type {
  App,
  BaseEntity,
  ChinaAppSysAudit,
  ChinaAppSysConfig,
  ChinaAppSysGroup,
  ChinaAppSysGroupCategory,
  ChinaAppSysGroupView,
  ChinaAppSysGruppenkennzeichen,
  ChinaAppSysLaufzeit,
  ChinaAppSysUser,
  ChinaAppSysView,
  ChinaAppSysViewField,
  Comment,
  Document,
} from './entities';
import {
  containerFieldNames,
  orderPositionFieldNames,
  receiptAssignmentFieldNames,
  receiptFieldNames,
} from './fieldTypes';

/**
 * Eigenschaftsnamen eines Typs. Der Parameter ist `Record<keyof T, true>`: Der Compiler
 * verlangt damit jeden Schlüssel und lehnt fremde ab — die Liste kann nicht gegenüber dem
 * Modell driften.
 */
function namesOf<T>(marks: Readonly<Record<keyof T, true>>): readonly string[] {
  return Object.keys(marks);
}

/** Wird in jede Entitätsliste gespiegelt — `BaseEntity` vererbt seine Eigenschaften. */
const baseEntityMarks: Readonly<Record<keyof BaseEntity, true>> = {
  Id: true,
  Created: true,
  CreatedByUserId: true,
  Modified: true,
  ModifiedByUserId: true,
  Timestamp: true,
};

const appNames = namesOf<App>({ Name: true, URI: true });

const baseEntityNames = namesOf<BaseEntity>(baseEntityMarks);

const chinaAppSysAuditNames = namesOf<ChinaAppSysAudit>({
  Id: true,
  EntityId: true,
  TableName: true,
  Type: true,
  DateTime: true,
  UserId: true,
  Username: true,
  AffectedColumns: true,
  OldValues: true,
  NewValues: true,
});

const chinaAppSysConfigNames = namesOf<ChinaAppSysConfig>({
  Key: true,
  Titel: true,
  Beschreibung: true,
  Value: true,
  Editable: true,
  Public: true,
  ...baseEntityMarks,
});

const chinaAppSysGroupNames = namesOf<ChinaAppSysGroup>({
  Bezeichnung: true,
  IstAdministrativ: true,
  Users: true,
  Views: true,
  ...baseEntityMarks,
});

const chinaAppSysGroupCategoryNames = namesOf<ChinaAppSysGroupCategory>({
  Bezeichnung: true,
  MenuSortierung: true,
  Views: true,
  ...baseEntityMarks,
});

const chinaAppSysGroupViewNames = namesOf<ChinaAppSysGroupView>({
  AllowAudit: true,
  AllowCreate: true,
  AllowDelete: true,
  AllowEdit: true,
  AllowRegiezentrum: true,
  GroupId: true,
  GroupBezeichnung: true,
  Group: true,
  ViewId: true,
  ViewBezeichnung: true,
  View: true,
  ...baseEntityMarks,
});

const chinaAppSysGruppenkennzeichenNames = namesOf<ChinaAppSysGruppenkennzeichen>({
  // Schreibweise der API — der Tippfehler ist Teil des JSON-Vertrags.
  Entitiy: true,
  Fieldname: true,
  Value: true,
  ...baseEntityMarks,
});

const chinaAppSysLaufzeitNames = namesOf<ChinaAppSysLaufzeit>({
  SchrittName: true,
  DauerTage: true,
  ...baseEntityMarks,
});

const chinaAppSysUserNames = namesOf<ChinaAppSysUser>({
  Name: true,
  EMail: true,
  Oid: true,
  BevorzugteSprache: true,
  HasAnyGroups: true,
  Groups: true,
  ...baseEntityMarks,
});

const chinaAppSysViewNames = namesOf<ChinaAppSysView>({
  ViewName: true,
  Entity: true,
  CategoryId: true,
  Category: true,
  CustomEndpoint: true,
  AdditionalFunctions: true,
  DefaultFilter: true,
  DefaultOrder: true,
  Fields: true,
  Groups: true,
  ...baseEntityMarks,
});

const chinaAppSysViewFieldNames = namesOf<ChinaAppSysViewField>({
  FieldName: true,
  FieldCaption: true,
  BezeichnungDe: true,
  BezeichnungChn: true,
  Visible: true,
  ReadOnly: true,
  Fixiert: true,
  Sortierung: true,
  SpaltenBreite: true,
  View: true,
  ...baseEntityMarks,
});

const commentNames = namesOf<Comment>({
  BestellpositionId: true,
  Bestellposition: true,
  Title: true,
  Body: true,
  From: true,
  ...baseEntityMarks,
});

const documentNames = namesOf<Document>({
  Bezeichnung: true,
  Beschreibung: true,
  Binary: true,
  BlobId: true,
  Token: true,
  FileName: true,
  FileExtension: true,
  MimeContentType: true,
  DateiGroesse: true,
  ContainerId: true,
  Container: true,
  ReceiptId: true,
  Receipt: true,
  ...baseEntityMarks,
});

const chinaAppValidationStatusNames = namesOf<ChinaAppValidationStatus>({
  Status: true,
  Message: true,
});

const bestellpositionDivideDefinitionNames = namesOf<BestellpositionDivideDefinition>({
  SourceId: true,
  Mengen: true,
});

const containerCloneDefinitionNames = namesOf<ContainerCloneDefinition>({
  SourceId: true,
  Duplikate: true,
});

const containerCloneDefinitionItemNames = namesOf<ContainerCloneDefinitionItem>({
  ContainerNo: true,
  ContTyp: true,
  Plombe: true,
});

/** Alle im Modell vorkommenden Eigenschaftsnamen in kanonischer PascalCase-Schreibweise. */
export const allPropertyNames: readonly string[] = [
  ...new Set([
    ...containerFieldNames,
    ...orderPositionFieldNames,
    ...receiptFieldNames,
    ...receiptAssignmentFieldNames,
    ...appNames,
    ...baseEntityNames,
    ...chinaAppSysAuditNames,
    ...chinaAppSysConfigNames,
    ...chinaAppSysGroupNames,
    ...chinaAppSysGroupCategoryNames,
    ...chinaAppSysGroupViewNames,
    ...chinaAppSysGruppenkennzeichenNames,
    ...chinaAppSysLaufzeitNames,
    ...chinaAppSysUserNames,
    ...chinaAppSysViewNames,
    ...chinaAppSysViewFieldNames,
    ...commentNames,
    ...documentNames,
    ...chinaAppValidationStatusNames,
    ...bestellpositionDivideDefinitionNames,
    ...containerCloneDefinitionNames,
    ...containerCloneDefinitionItemNames,
  ]),
];
